import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "microeconomics-sem-1-unit-4-production-costs-isoquants-returns-to-scale",
  title: "Introductory Microeconomics Unit 4: Production Function, Isoquants & Cost Curves Notes",
  description: "Detailed DU Sem 1 Unit 4 notes covering Law of Variable Proportions, Isoquants, MRTS, Returns to Scale, Euler's Theorem, Isocosts, Cost Minimization & LAC Envelope.",
  pubDate: "2025-08-22",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["Production Theory", "Isoquants", "Cost Curves", "Returns to Scale", "Microeconomics", "DU Notes"],
  readMinutes: 17,
  heroEyebrow: "Sem 1 · Unit 4 Notes"
};

export function Body() {
  return (
    <>
      <p>
        Welcome to Unit 4 of Delhi University's Introductory Microeconomics for Semester 1. This unit shifts our focus from the consumer's side (demand) to the producer's side (supply). We explore the technical relationship between inputs and output (Production Theory) and the economic relationship between output and costs (Cost Theory). A solid grasp of this unit is non-negotiable for understanding how firms make decisions to maximize profits or minimize costs.
      </p>

      <h2>1. The Production Function</h2>
      <p>
        A production function is a purely technical or engineering relationship between inputs (factors of production) and the maximum output that can be produced using those inputs, given the current state of technology. 
      </p>
      <p>
        Mathematically, it is expressed as: <strong>Q = f(L, K, T, E...)</strong>, where Q is the total physical product (output), L is labor, K is capital, T is land (or resources), and E is entrepreneurship. For simplicity in introductory microeconomics, we assume a two-input model: Labor (L) and Capital (K). Thus, <strong>Q = f(L, K)</strong>.
      </p>

      <h3>Total, Average, and Marginal Product</h3>
      <p>
        To understand production in the short run (where at least one input is fixed, typically capital K), we need to define three crucial concepts associated with the variable input (labor L).
      </p>
      <ul>
        <li><strong>Total Product (TP or TPP):</strong> The total amount of output produced in a given time period by a given quantity of inputs. TP = Q = f(L, K_bar).</li>
        <li><strong>Average Product (AP or APP):</strong> The total product per unit of the variable input. AP = TP / L. It represents the average productivity of labor.</li>
        <li><strong>Marginal Product (MP or MPP):</strong> The addition to total product resulting from the employment of one more unit of the variable input, keeping the fixed input constant. MP = d(TP) / dL. Geometrically, MP is the slope of the TP curve.</li>
      </ul>

      <h3>Exact Mathematical and Geometric Relationships</h3>
      <p>
        The relationship between TP, AP, and MP is a favorite topic for DU examiners. You must understand the mathematical logic and the geometry of the curves.
      </p>
      <ul>
        <li><strong>Relationship between TP and MP:</strong>
          <ul>
            <li>As long as MP is positive, TP increases.</li>
            <li>When MP is increasing, TP increases at an increasing rate (convex portion of the TP curve).</li>
            <li>When MP reaches its maximum, the TP curve has an inflection point (changes from convex to concave).</li>
            <li>When MP is decreasing but still positive, TP increases at a decreasing rate.</li>
            <li>When MP is exactly zero, TP reaches its maximum point.</li>
            <li>When MP becomes negative, TP starts to decrease.</li>
          </ul>
        </li>
        <li><strong>Relationship between AP and MP:</strong>
          <ul>
            <li>When MP is greater than AP (MP &gt; AP), AP must be rising. Think of your GPA: if your new semester grade (Marginal) is higher than your cumulative GPA (Average), your cumulative GPA will rise.</li>
            <li>When MP is less than AP (MP &lt; AP), AP must be falling.</li>
            <li>When MP equals AP (MP = AP), AP is at its maximum. Geometrically, the MP curve cuts the AP curve from above at the AP curve's peak. Also, at the maximum of AP, a ray from the origin is tangent to the TP curve.</li>
          </ul>
        </li>
      </ul>

      <h2>2. Short-Run Production: The Law of Variable Proportions</h2>
      <p>
        The Law of Variable Proportions (also known as the Law of Diminishing Marginal Returns) is the fundamental principle of short-run production. It states that as more and more units of a variable factor (e.g., Labor) are combined with a fixed amount of other factors (e.g., Capital), the marginal product of the variable factor will eventually decline.
      </p>
      
      <h3>The Three Stages of Production</h3>
      <p>
        The production process in the short run is typically divided into three distinct stages based on the behavior of TP, AP, and MP.
      </p>
      <ul>
        <li><strong>Stage I: Stage of Increasing Returns</strong>
          <ul>
            <li>Starts from the origin (L=0) to the point where AP reaches its maximum (where AP = MP).</li>
            <li>In this stage, TP increases initially at an increasing rate (up to the point of inflection where MP is maximum) and then at a decreasing rate.</li>
            <li>MP rises, reaches a peak, and starts falling, but remains above AP. AP continues to rise throughout this stage.</li>
            <li>Why does this happen? The fixed factor (Capital) is abundant relative to the variable factor (Labor). Adding labor allows for better utilization of machinery and specialization of tasks.</li>
          </ul>
        </li>
        <li><strong>Stage II: Stage of Diminishing Returns (The Economic Zone)</strong>
          <ul>
            <li>Starts from the point where AP is maximum (AP = MP) to the point where TP is maximum (MP = 0).</li>
            <li>In this stage, TP continues to increase, but strictly at a decreasing rate.</li>
            <li>Both AP and MP are declining, but both are positive. MP is strictly less than AP.</li>
            <li>Why does this happen? The ideal proportion between Labor and Capital has been passed. The fixed factor becomes increasingly scarce relative to the variable factor.</li>
          </ul>
        </li>
        <li><strong>Stage III: Stage of Negative Returns</strong>
          <ul>
            <li>Starts beyond the point of maximum TP (where MP becomes negative).</li>
            <li>TP declines.</li>
            <li>AP continues to fall but remains positive. MP is negative.</li>
            <li>Why does this happen? The variable factor is so excessive relative to the fixed factor that workers get in each other's way, reducing total output.</li>
          </ul>
        </li>
      </ul>

      <h3>Rational Economic Zone (Why Stage II?)</h3>
      <p>
        A rational producer will never choose to operate in Stage III because adding more workers actually reduces total output (MP &lt; 0), which makes absolutely no economic sense. But why not Stage I? In Stage I, the marginal product of the fixed factor is technically negative. The firm is not using its fixed capital efficiently. As long as the product price is positive, a firm in a competitive market will want to expand output through Stage I because the average product of labor is still rising, meaning the cost per unit of output is falling. 
      </p>
      <p>
        Therefore, Stage II is the only relevant stage for a rational producer. In Stage II, MP and AP are falling, but both are positive. The exact level of employment within Stage II will be determined by input and output prices.
      </p>

      <h2>3. Long-Run Production: Isoquants and MRTS</h2>
      <p>
        In the long run, all factors of production are variable. The firm can scale up or scale down both Labor and Capital. We analyze long-run production using Isoquants.
      </p>

      <h3>What is an Isoquant?</h3>
      <p>
        An isoquant (iso = equal, quant = quantity) is a curve representing all the different combinations of two inputs (Labor and Capital) that yield exactly the same level of total output. It is the producer's equivalent of an indifference curve.
      </p>

      <h3>Properties of Isoquants</h3>
      <ul>
        <li><strong>Downward Sloping (Negative Slope):</strong> If you reduce the amount of capital, you must increase the amount of labor to keep total output constant.</li>
        <li><strong>Convex to the Origin:</strong> This is due to the principle of Diminishing Marginal Rate of Technical Substitution (MRTS). As you substitute more and more labor for capital, it becomes increasingly difficult to replace capital with labor.</li>
        <li><strong>Higher Isoquants Mean Higher Output:</strong> An isoquant lying to the right (further from the origin) represents combinations of inputs that produce a greater total quantity.</li>
        <li><strong>Isoquants Cannot Intersect:</strong> If they intersected, it would imply that a single combination of labor and capital could produce two different maximum levels of output efficiently, which violates our assumption of technical efficiency.</li>
      </ul>

      <h3>Marginal Rate of Technical Substitution (MRTS)</h3>
      <p>
        MRTS is the rate at which a firm can substitute one input for another while keeping output constant. It is the absolute value of the slope of the isoquant.
      </p>
      <p>
        <strong>MRTS_LK = - dK / dL</strong> (The amount of capital a firm is willing to give up to get one more unit of labor).
      </p>
      <p>
        Crucially, MRTS_LK is equal to the ratio of the marginal products of the two inputs:
      </p>
      <p>
        Along an isoquant, the change in total output is zero. Therefore: <br/>
        (MP_L * dL) + (MP_K * dK) = 0 <br/>
        MP_L * dL = - MP_K * dK <br/>
        - dK / dL = MP_L / MP_K <br/>
        <strong>MRTS_LK = MP_L / MP_K</strong>
      </p>
      <p>
        The diminishing MRTS_LK implies that as we move down along an isoquant, MP_L falls (due to more labor) and MP_K rises (due to less capital), causing the ratio (MP_L / MP_K) to decline. This creates the convex shape.
      </p>

      <h3>Ridge Lines and the Economic Region of Production</h3>
      <p>
        While an isoquant theoretically extends indefinitely, certain parts of it represent irrational combinations. Ridge lines separate the economically relevant portions of isoquants from the irrelevant ones.
      </p>
      <ul>
        <li><strong>Upper Ridge Line:</strong> Connects points on isoquants where the marginal product of capital (MP_K) is zero. Above this line, MP_K is negative (meaning MRTS is negative, isoquants slope upwards).</li>
        <li><strong>Lower Ridge Line:</strong> Connects points on isoquants where the marginal product of labor (MP_L) is zero. To the right of this line, MP_L is negative.</li>
      </ul>
      <p>
        The area bounded by the two ridge lines is the <strong>Economic Region of Production</strong>. Inside this region, the marginal products of both inputs are positive, isoquants are downward sloping, and it is rational to operate here. This is the long-run equivalent of Stage II.
      </p>

      <h2>4. Returns to Scale</h2>
      <p>
        Returns to scale describe how total output changes when all inputs are changed proportionately in the long run. If a firm doubles both labor and capital, what happens to output?
      </p>
      <ul>
        <li><strong>Increasing Returns to Scale (IRS):</strong> Output increases by a greater proportion than the increase in inputs. E.g., doubling inputs triples output. This is usually due to indivisibilities, specialization, and dimensional efficiencies (like the area/volume rule in pipelines).</li>
        <li><strong>Constant Returns to Scale (CRS):</strong> Output increases by exactly the same proportion as the inputs. E.g., doubling inputs exactly doubles output. This means replicating the current plant precisely.</li>
        <li><strong>Decreasing Returns to Scale (DRS):</strong> Output increases by a smaller proportion than the increase in inputs. E.g., doubling inputs only increases output by 50%. This is typically caused by managerial diseconomies—communication breakdowns, bureaucracy, and coordination failures as a firm gets too large.</li>
      </ul>

      <h3>Cobb-Douglas Production Function and Homogeneity</h3>
      <p>
        The Cobb-Douglas production function is a widely used functional form: <strong>Q = A * K^alpha * L^beta</strong>.
      </p>
      <p>
        Where A is total factor productivity, and alpha and beta are output elasticities of capital and labor, respectively.
      </p>
      <p>
        A function is homogeneous of degree <em>n</em> if f(tL, tK) = t^n * f(L, K) for any t &gt; 0. Let's test the Cobb-Douglas function by multiplying both inputs by a constant 't':
      </p>
      <p>
        Q_new = A * (tK)^alpha * (tL)^beta <br/>
        Q_new = A * (t^alpha * K^alpha) * (t^beta * L^beta) <br/>
        Q_new = t^(alpha + beta) * [A * K^alpha * L^beta] <br/>
        Q_new = t^(alpha + beta) * Q_initial
      </p>
      <p>
        Thus, the Cobb-Douglas function is homogeneous of degree (alpha + beta).
      </p>
      <ul>
        <li>If alpha + beta = 1: Constant Returns to Scale (CRS)</li>
        <li>If alpha + beta &gt; 1: Increasing Returns to Scale (IRS)</li>
        <li>If alpha + beta &lt; 1: Decreasing Returns to Scale (DRS)</li>
      </ul>

      <h3>Euler's Product Exhaustion Theorem</h3>
      <p>
        Euler's Theorem states that for a production function that is homogeneous of degree 1 (CRS), if each factor of production is paid its marginal product, the total output will be exactly exhausted.
      </p>
      <p>
        Mathematically: <strong>Q = L * MP_L + K * MP_K</strong>
      </p>
      <p>
        Under perfect competition in input and output markets, real wage (w/P) = MP_L and real rental rate (r/P) = MP_K. Therefore, paying factors their marginal product means total wage bill plus total capital rent exactly equals total revenue (leaving zero pure economic profit). This is a fascinating result linking production theory to income distribution.
      </p>

      <h2>5. Isocost Lines and Cost Minimization</h2>
      <p>
        To find the optimal production point, we must combine technical possibilities (isoquants) with economic constraints (costs). The constraint is represented by the isocost line.
      </p>

      <h3>The Isocost Line</h3>
      <p>
        An isocost line shows all combinations of labor and capital that a firm can purchase for a given total cost, given input prices.
      </p>
      <p>
        Equation: <strong>C = wL + rK</strong>
      </p>
      <p>
        Where C is total cost, w is the wage rate of labor, and r is the rental rate of capital. Rearranging for K gives the equation of a line:
      </p>
      <p>
        <strong>K = (C / r) - (w / r) * L</strong>
      </p>
      <p>
        The vertical intercept is C/r (maximum capital if no labor is hired). The horizontal intercept is C/w. The slope of the isocost line is <strong>- w / r</strong>, the ratio of input prices.
      </p>

      <h3>Cost Minimization (Producer's Equilibrium)</h3>
      <p>
        A firm aims to produce a target level of output (a specific isoquant) at the lowest possible cost (the lowest possible isocost line). 
      </p>
      <p>
        Geometrically, this occurs at the point of tangency between the target isoquant and the lowest possible isocost line. At the point of tangency, the slopes are equal.
      </p>
      <p>
        Slope of Isoquant (MRTS) = Slope of Isocost line <br/>
        <strong>MP_L / MP_K = w / r</strong>
      </p>
      <p>
        Rearranging this gives a powerful intuitive rule:
        <strong>MP_L / w = MP_K / r</strong>
      </p>
      <p>
        This means the firm should allocate its budget such that the marginal product per rupee spent on labor equals the marginal product per rupee spent on capital. If MP_L/w &gt; MP_K/r, the firm should shift spending from capital to labor because labor is giving more "bang for the buck" at the margin.
      </p>

      <h3>The Expansion Path</h3>
      <p>
        If we hold input prices (w and r) constant but vary the firm's budget, the isocost lines shift outwards in parallel. The firm will find new points of tangency on higher isoquants. The curve connecting all these points of tangency (cost-minimizing input combinations for every output level) is called the <strong>Expansion Path</strong>. It represents the firm's long-run total cost curve mapped in the input space. For a homogeneous production function, the expansion path is a straight line through the origin.
      </p>

      <h2>6. Short-Run Cost Curves</h2>
      <p>
        The short run implies the existence of fixed costs. Let's break down the cost family.
      </p>
      <ul>
        <li><strong>Total Fixed Cost (TFC):</strong> Costs that do not vary with output (e.g., rent, insurance). The TFC curve is a horizontal line.</li>
        <li><strong>Total Variable Cost (TVC):</strong> Costs that vary with output (e.g., wages, raw materials). The TVC curve starts from the origin, initially rises at a decreasing rate, and then rises at an increasing rate (due to the Law of Variable Proportions).</li>
        <li><strong>Total Cost (TC):</strong> TC = TFC + TVC. The TC curve is parallel to the TVC curve, shifted vertically upwards by the amount of TFC.</li>
      </ul>

      <h3>Average and Marginal Costs</h3>
      <ul>
        <li><strong>Average Fixed Cost (AFC):</strong> AFC = TFC / Q. Since TFC is constant, as Q increases, AFC continuously falls. The AFC curve is a <em>rectangular hyperbola</em>; it approaches both axes asymptotically but never touches them. The area under the AFC curve at any point equals TFC.</li>
        <li><strong>Average Variable Cost (AVC):</strong> AVC = TVC / Q. AVC typically falls, reaches a minimum, and then rises, giving it a U-shape. This U-shape is the exact mirror image of the AP curve. (When AP rises, AVC falls; when AP is maximum, AVC is minimum).</li>
        <li><strong>Average Total Cost (ATC or AC):</strong> ATC = TC / Q = AFC + AVC. It is U-shaped. The minimum of ATC occurs at a higher level of output than the minimum of AVC because falling AFC pulls ATC down for a while even after AVC starts rising.</li>
        <li><strong>Marginal Cost (MC):</strong> The addition to total cost from producing one more unit. MC = d(TC)/dQ = d(TVC)/dQ. The MC curve is U-shaped and mirrors the MP curve.</li>
      </ul>

      <h3>Crucial Geometric Relationships in Short-Run Costs</h3>
      <p>
        These relationships are standard 5-mark DU exam questions.
      </p>
      <ol>
        <li><strong>MC cuts AVC at its minimum.</strong> (Proof: When MC &lt; AVC, AVC falls. When MC &gt; AVC, AVC rises. Therefore, they must be equal at the minimum of AVC).</li>
        <li><strong>MC cuts ATC at its minimum.</strong> (Same logic as above).</li>
        <li><strong>ATC and AVC get closer together but never touch.</strong> The vertical distance between them is AFC. Since AFC gets smaller as Q increases, the gap narrows, but since AFC never reaches zero, they never intersect.</li>
        <li><strong>Relationship between Production and Cost:</strong> MC = w / MP_L and AVC = w / AP_L. This proves that cost curves are just the inverse of productivity curves. The U-shape of short-run cost curves is driven entirely by the Law of Variable Proportions.</li>
      </ol>

      <h2>7. Long-Run Cost Curves: The LAC Envelope</h2>
      <p>
        In the long run, there are no fixed costs. The firm can choose any plant size. For every possible plant size, there is a Short-Run Average Cost (SAC) curve.
      </p>

      <h3>The Long-Run Average Cost (LAC) Envelope Curve</h3>
      <p>
        The LAC curve is the locus of the cost-minimizing points for each level of output. It is derived by drawing a curve that is tangent to the lowest possible SAC curve for each output level. Because it "envelopes" all the SAC curves from below, it is called the Envelope Curve or the Planning Curve.
      </p>
      <p>
        A key insight: The LAC curve is tangent to the minimum point of an SAC curve only at the absolute minimum of the LAC curve itself. For output levels below the LAC minimum, the LAC is tangent to the falling portion of the SAC curves. For output levels above the LAC minimum, the LAC is tangent to the rising portion of the SAC curves.
      </p>

      <h3>Economies and Diseconomies of Scale</h3>
      <p>
        The LAC curve is U-shaped (often a flatter, "saucer" shape), but not because of the Law of Variable Proportions (which requires fixed factors). The long-run U-shape is driven by Returns to Scale and Economies/Diseconomies of scale.
      </p>
      <ul>
        <li><strong>Internal Economies of Scale:</strong> Causes the LAC curve to fall as output expands. Sources include technical economies (indivisibilities), managerial economies (specialization), commercial economies (bulk buying discounts), and financial economies (lower interest rates for large firms).</li>
        <li><strong>Internal Diseconomies of Scale:</strong> Causes the LAC curve to rise. The primary source is managerial diseconomies—the difficulty of coordinating and controlling a massive, complex organization, leading to inefficiencies and red tape.</li>
      </ul>
      <p>
        (Note: External economies/diseconomies shift the entire LAC curve down/up, whereas internal economies move you along the LAC curve).
      </p>

      <h2>8. Fully Solved Numerical Problems</h2>
      <p>
        Practicing numericals is critical. Here are 8 solved examples covering DU exam patterns.
      </p>

      <div className="bg-gray-100 p-6 rounded-lg my-6 dark:bg-gray-800">
        <h4 className="text-xl font-bold mb-4">Problem 1: Deriving AP and MP</h4>
        <p><strong>Q:</strong> Given the production function Q = 6L^2 - 0.2L^3. Find the expressions for Average Product of Labor (AP_L) and Marginal Product of Labor (MP_L). Determine the level of labor where AP_L is maximized.</p>
        <p><strong>Solution:</strong></p>
        <p>1. AP_L = Q / L = (6L^2 - 0.2L^3) / L = 6L - 0.2L^2</p>
        <p>2. MP_L = dQ / dL = d(6L^2 - 0.2L^3) / dL = 12L - 0.6L^2</p>
        <p>3. To find where AP_L is maximized, we set its derivative to zero:</p>
        <p>d(AP_L) / dL = 6 - 0.4L = 0  =&gt;  0.4L = 6  =&gt; L = 15 units.</p>
        <p><em>Check:</em> Alternatively, we know AP_L is max when AP_L = MP_L.</p>
        <p>6L - 0.2L^2 = 12L - 0.6L^2</p>
        <p>0.4L^2 - 6L = 0  =&gt;  L(0.4L - 6) = 0. Ignoring L=0, we get 0.4L = 6 =&gt; L = 15. The answers match perfectly.</p>
      </div>

      <div className="bg-gray-100 p-6 rounded-lg my-6 dark:bg-gray-800">
        <h4 className="text-xl font-bold mb-4">Problem 2: Cost Minimization</h4>
        <p><strong>Q:</strong> A firm has the production function Q = 50 * L^0.5 * K^0.5. The price of labor (w) is 20 and the price of capital (r) is 80. The firm wishes to produce Q = 1000 units. Find the cost-minimizing combination of L and K, and the total cost.</p>
        <p><strong>Solution:</strong></p>
        <p>1. Target output: 1000 = 50 * L^0.5 * K^0.5 =&gt; 20 = L^0.5 * K^0.5 =&gt; 400 = L * K =&gt; K = 400/L (Eq. 1)</p>
        <p>2. Tangency condition: MP_L / MP_K = w / r</p>
        <p>MP_L = dQ/dL = 25 * L^(-0.5) * K^0.5</p>
        <p>MP_K = dQ/dK = 25 * L^0.5 * K^(-0.5)</p>
        <p>MRTS = MP_L / MP_K = (K^0.5 / L^0.5) / (L^0.5 / K^(-0.5)) = K / L</p>
        <p>3. Equating MRTS to w/r: K / L = 20 / 80 = 1/4 =&gt; L = 4K (Eq. 2) (Expansion Path equation)</p>
        <p>4. Substitute Eq. 2 into Eq. 1:</p>
        <p>400 = (4K) * K =&gt; 4K^2 = 400 =&gt; K^2 = 100 =&gt; K = 10 units.</p>
        <p>5. Find L: L = 4(10) = 40 units.</p>
        <p>6. Minimum Total Cost: C = wL + rK = (20 * 40) + (80 * 10) = 800 + 800 = 1600.</p>
      </div>

      <div className="bg-gray-100 p-6 rounded-lg my-6 dark:bg-gray-800">
        <h4 className="text-xl font-bold mb-4">Problem 3: Finding Shutdown and Break-Even Points</h4>
        <p><strong>Q:</strong> A competitive firm has a short-run total cost curve given by TC = 100 + 10Q + 2Q^2. Find the break-even price and the shutdown price.</p>
        <p><strong>Solution:</strong></p>
        <p>1. Fixed Cost (TFC) = 100; Variable Cost (TVC) = 10Q + 2Q^2.</p>
        <p>2. MC = d(TC)/dQ = 10 + 4Q</p>
        <p>3. <strong>Break-Even Price:</strong> Minimum of ATC. <br/> ATC = TC/Q = (100/Q) + 10 + 2Q. <br/> Minimum ATC occurs where MC = ATC. <br/> 10 + 4Q = (100/Q) + 10 + 2Q =&gt; 2Q = 100/Q =&gt; 2Q^2 = 100 =&gt; Q^2 = 50 =&gt; Q = 7.07. <br/> Break-even price (min ATC) = 10 + 4(7.07) = 38.28.</p>
        <p>4. <strong>Shutdown Price:</strong> Minimum of AVC. <br/> AVC = TVC/Q = 10 + 2Q. <br/> Minimum AVC occurs where MC = AVC (or just look at the AVC equation; since Q must be positive, the min AVC is at Q=0). <br/> 10 + 4Q = 10 + 2Q =&gt; 2Q = 0 =&gt; Q = 0. <br/> Shutdown price (min AVC at Q=0) = 10.</p>
      </div>

      <div className="bg-gray-100 p-6 rounded-lg my-6 dark:bg-gray-800">
        <h4 className="text-xl font-bold mb-4">Problem 4: Returns to Scale Evaluation</h4>
        <p><strong>Q:</strong> Determine the returns to scale for the following production functions: (a) Q = L^0.4 * K^0.6, (b) Q = L^2 + K^2, (c) Q = 10 * L^0.3 * K^0.5.</p>
        <p><strong>Solution:</strong> Test for homogeneity by scaling inputs by t.</p>
        <p>(a) Q = L^0.4 * K^0.6. Cobb-Douglas. alpha+beta = 0.4 + 0.6 = 1. Constant Returns to Scale (CRS).</p>
        <p>(b) f(tL, tK) = (tL)^2 + (tK)^2 = t^2 * (L^2 + K^2) = t^2 * Q. Homogeneous of degree 2. Since 2 &gt; 1, Increasing Returns to Scale (IRS).</p>
        <p>(c) Q = 10 * L^0.3 * K^0.5. Cobb-Douglas. alpha+beta = 0.3 + 0.5 = 0.8. Since 0.8 &lt; 1, Decreasing Returns to Scale (DRS).</p>
      </div>

      <div className="bg-gray-100 p-6 rounded-lg my-6 dark:bg-gray-800">
        <h4 className="text-xl font-bold mb-4">Problem 5: Marginal Rate of Technical Substitution</h4>
        <p><strong>Q:</strong> A firm's production function is Q = min(3L, 4K). What type of production function is this, and what is its MRTS?</p>
        <p><strong>Solution:</strong></p>
        <p>This is a Leontief (fixed-proportion) production function. The inputs must be used in a fixed ratio. The isoquants are L-shaped.</p>
        <p>Because the inputs cannot be substituted for one another to keep output constant along the efficient point (the corner of the L-shape), the Marginal Rate of Technical Substitution (MRTS) is either 0 (on the horizontal segment) or infinity (on the vertical segment), and undefined at the corner.</p>
      </div>
      
      <div className="bg-gray-100 p-6 rounded-lg my-6 dark:bg-gray-800">
        <h4 className="text-xl font-bold mb-4">Problem 6: Long-Run Cost Function from Cobb-Douglas</h4>
        <p><strong>Q:</strong> Given Q = L^0.5 * K^0.5. Input prices are w and r. Derive the long-run total cost function C(Q).</p>
        <p><strong>Solution:</strong></p>
        <p>1. Cost minimization: MRTS = w/r =&gt; K/L = w/r =&gt; L = (r/w)K.</p>
        <p>2. Substitute into Production Function to express inputs as functions of output Q:</p>
        <p>Q = [(r/w)K]^0.5 * K^0.5 = (r/w)^0.5 * K.</p>
        <p>Therefore, K*(Q) = Q * (w/r)^0.5.</p>
        <p>3. Find L*(Q): L = (r/w) * [Q * (w/r)^0.5] = Q * (r/w)^0.5.</p>
        <p>4. Total Cost C = wL* + rK* = w[Q * (r/w)^0.5] + r[Q * (w/r)^0.5] = Q(w^0.5 * r^0.5) + Q(r^0.5 * w^0.5) = 2Q(wr)^0.5.</p>
        <p>This shows that Long Run Total Cost is a linear function of Q, implying Constant AC and MC (which matches the CRS property of this production function).</p>
      </div>

      <div className="bg-gray-100 p-6 rounded-lg my-6 dark:bg-gray-800">
        <h4 className="text-xl font-bold mb-4">Problem 7: Maximizing Output given a Budget Constraint</h4>
        <p><strong>Q:</strong> A firm has a budget of Rs. 3000 to spend on Labor and Capital. The price of Labor w=50, and price of Capital r=100. The production function is Q = 10 * L * K. Find the output-maximizing quantities of L and K.</p>
        <p><strong>Solution:</strong></p>
        <p>1. Budget Constraint: 3000 = 50L + 100K.</p>
        <p>2. Optimization condition: MP_L / MP_K = w / r</p>
        <p>MP_L = 10K; MP_K = 10L.</p>
        <p>MRTS = 10K / 10L = K / L.</p>
        <p>Equating to w/r: K / L = 50 / 100 = 1/2 =&gt; L = 2K.</p>
        <p>3. Substitute into the budget constraint:</p>
        <p>3000 = 50(2K) + 100K =&gt; 3000 = 100K + 100K =&gt; 3000 = 200K =&gt; K = 15.</p>
        <p>4. Find L: L = 2(15) = 30.</p>
        <p>5. Maximum Output: Q = 10 * (30) * (15) = 4500 units.</p>
      </div>

      <div className="bg-gray-100 p-6 rounded-lg my-6 dark:bg-gray-800">
        <h4 className="text-xl font-bold mb-4">Problem 8: Euler's Theorem Verification</h4>
        <p><strong>Q:</strong> Verify Euler's theorem for the production function Q = A * L^0.6 * K^0.4.</p>
        <p><strong>Solution:</strong></p>
        <p>Euler's theorem states: L(dQ/dL) + K(dQ/dK) = Q (for a degree 1 homogeneous function).</p>
        <p>1. Find MP_L (dQ/dL): 0.6 * A * L^(-0.4) * K^0.4</p>
        <p>2. Find MP_K (dQ/dK): 0.4 * A * L^0.6 * K^(-0.6)</p>
        <p>3. Compute L(MP_L) + K(MP_K):</p>
        <p>L * [0.6 * A * L^(-0.4) * K^0.4] + K * [0.4 * A * L^0.6 * K^(-0.6)]</p>
        <p>= 0.6 * A * L^0.6 * K^0.4 + 0.4 * A * L^0.6 * K^0.4</p>
        <p>= (0.6 + 0.4) * [A * L^0.6 * K^0.4]</p>
        <p>= 1 * Q = Q.</p>
        <p>Theorem verified. The payments to factors exhaust the total product.</p>
      </div>

      <h2>9. DU Exam Tips & 15-Mark Model Outline</h2>
      <p>
        Production and Costs frequently account for heavy-weightage questions in DU's final exams.
      </p>

      <h3>Crucial Diagram Checklist</h3>
      <p>Before you enter the exam hall, ensure you can draw these flawlessly without looking at the book:</p>
      <ul>
        <li><strong>TP, AP, MP curves stacked:</strong> Must clearly show the inflection point aligning with max MP, and max AP intersecting MP. Label the three stages clearly.</li>
        <li><strong>Isoquants and Ridge Lines:</strong> Show the convex isoquants and highlight the economic region in the middle.</li>
        <li><strong>Producer's Equilibrium:</strong> Isocost line tangent to an isoquant.</li>
        <li><strong>Expansion Path:</strong> Draw a few tangency points and connect them.</li>
        <li><strong>Short-run Cost curves stacked:</strong> MC cutting ATC and AVC exactly at their minimum points. AFC as a rectangular hyperbola.</li>
        <li><strong>LAC Envelope Curve:</strong> Draw 3-4 SAC curves and smoothly envelope them with a U-shaped LAC curve. Emphasize the tangency points correctly.</li>
      </ul>

      <h3>Model Answer Outline (15 Marks)</h3>
      <p><strong>Question:</strong> "Explain the Law of Variable Proportions. Why is Stage II considered the rational zone of production?"</p>
      <p><strong>Outline:</strong></p>
      <ol>
        <li><strong>Introduction (2 marks):</strong> Define the law. State assumptions (short run, one factor fixed, homogenous variable units, constant technology).</li>
        <li><strong>Table Schedule (2 marks):</strong> Create a simple hypothetical table showing L, TP, AP, and MP. Ensure the numbers reflect the three stages (increasing, diminishing, and negative returns).</li>
        <li><strong>Diagram (4 marks):</strong> Draw a large, neat graph with TP on the top panel and AP, MP on the bottom panel. Clearly demarcate Stage I, II, and III. Mark the point of inflection, max AP, max TP.</li>
        <li><strong>Explanation of Stages (4 marks):</strong> Briefly explain what happens to TP, AP, and MP in each stage and provide the economic intuition (e.g., better utilization of fixed factor in Stage I, over-crowding in Stage III).</li>
        <li><strong>Rational Zone Justification (3 marks):</strong> Argue why Stage I is irrational (underutilization of fixed capital, MP of fixed factor is negative). Argue why Stage III is irrational (MP of labor is negative, total output falls). Conclude that Stage II (diminishing but positive returns for both factors) is the only economically rational operating region.</li>
      </ol>

      <p>
        Mastering Unit 4 is largely about understanding the duality between production and costs. Every concept in production has a direct mirror image in costs. If you understand the MP curve, you understand the MC curve. If you grasp Isoquants, you will breeze through Isocosts and Expansion Paths. Keep practicing the numericals and sketching the graphs!
      </p>

    </>
  );
}



